import type { Trade } from '@/mocks/technical';

function norm(key: string): string {
  return key.toLowerCase().replace(/[^a-z]/g, '');
}

function pick(row: Record<string, unknown>, keys: string[]): unknown {
  for (const k of Object.keys(row)) {
    if (keys.includes(norm(k))) return row[k];
  }
  return undefined;
}

function toNumber(v: unknown): number {
  if (typeof v === 'number') return v;
  if (typeof v === 'string') {
    const n = parseFloat(v.replace(/[^0-9.-]/g, ''));
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
}

function toDate(v: unknown): string {
  if (v instanceof Date) return v.toISOString().slice(0, 10);
  const s = String(v ?? '').trim();
  const m = s.match(/(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})/);
  if (m) return `${m[1]}-${m[2].padStart(2, '0')}-${m[3].padStart(2, '0')}`;
  return new Date().toISOString().slice(0, 10);
}

function toDirection(v: unknown): 'long' | 'short' {
  const s = String(v ?? '').toLowerCase();
  if (s.includes('short') || s === 'sell' || s === 'bán' || s === 'ban') return 'short';
  if (s.includes('long') || s === 'buy' || s === 'mua') return 'long';
  return 'long';
}

function toSession(v: unknown): 'Asia' | 'London' | 'New York' | undefined {
  const s = String(v ?? '').toLowerCase();
  if (s.includes('asia')) return 'Asia';
  if (s.includes('london')) return 'London';
  if (s.includes('new') || s.includes('york') || s.includes('ny')) return 'New York';
  return undefined;
}

export function parseTradeRows(rows: Record<string, unknown>[]): Trade[] {
  const trades: Trade[] = [];
  for (const row of rows) {
    const date = pick(row, ['date', 'ngay', 'time', 'opened']);
    const pnl = pick(row, ['pnl', 'profit', 'profitloss', 'loinhuan', 'result', 'gain']);
    const lot = pick(row, ['lot', 'lots', 'volume', 'size']);
    const dir = pick(row, ['direction', 'dir', 'type', 'side', 'huong']);
    const session = pick(row, ['session', 'phien', 'timeframe']);

    // A row must at least have a date or a P/L to be considered a trade.
    if (pnl === undefined && date === undefined) continue;

    const pnlVal = toNumber(pnl);
    const dirVal = toDirection(dir);
    // If profit/loss is provided as separate columns, reconcile.
    const pl = pick(row, ['pl', 'netpl', 'netprofit']);
    const finalPnl = pl !== undefined ? toNumber(pl) : pnlVal;

    trades.push({
      date: toDate(date),
      dir: dirVal,
      symbol: String(pick(row, ['symbol', 'pair', 'instrument', 'cap']) ?? 'N/A'),
      lot: toNumber(lot) || 0.01,
      pnl: finalPnl,
      session: toSession(session),
    });
  }
  return trades;
}